

/**
=========================================================
* Material Dashboard 2 React - v2.2.0
=========================================================

* Product Page: https://www.creative-tim.com/product/material-dashboard-react
* Copyright 2023 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/

/**
 * The base typography styles for the Material Dashboard 2 React.
 * You can add new typography style using this file.
 * You can customized the typography styles for the entire Material Dashboard 2 React using thie file.
 */

// Material Dashboard 2 React Base Styles
import colors from "assets/theme/base/colors";
import { FONT_MONO, monoTabularProps } from "styles/tokens/typography";

// Material Dashboard 2 React Helper Functions
import pxToRem from "assets/theme/functions/pxToRem";

const { dark, surface } = colors;

const baseProperties = {
  displayFontFamily: '"Sora", "Inter", "Helvetica", "Arial", sans-serif',
  bodyFontFamily: '"Hanken Grotesk", "Inter", "Helvetica", "Arial", sans-serif',
  codeFontFamily: FONT_MONO,
  fontWeightLighter: 100,
  fontWeightLight: 300,
  fontWeightRegular: 400,
  fontWeightMedium: 600,
  fontWeightBold: 700,
  fontSizeXXS: pxToRem(10.4),
  fontSizeXS: pxToRem(12),
  fontSizeSM: pxToRem(14),
  fontSizeMD: pxToRem(16),
  fontSizeLG: pxToRem(18),
  fontSizeXL: pxToRem(20),
  fontSize2XL: pxToRem(24),
  fontSize3XL: pxToRem(30),
};

const baseHeadingProperties = {
  fontFamily: baseProperties.displayFontFamily,
  color: dark.main,
  fontWeight: baseProperties.fontWeightBold,
};

const baseDisplayProperties = {
  fontFamily: baseProperties.displayFontFamily,
  color: dark.main,
  fontWeight: 600,
  lineHeight: 1.05,
};

const typography = {
  fontFamily: baseProperties.bodyFontFamily,
  fontWeightLighter: baseProperties.fontWeightLighter,
  fontWeightLight: baseProperties.fontWeightLight,
  fontWeightRegular: baseProperties.fontWeightRegular,
  fontWeightMedium: baseProperties.fontWeightMedium,
  fontWeightBold: baseProperties.fontWeightBold,

  h1: {
    fontSize: pxToRem(48),
    lineHeight: 1.25,
    ...baseHeadingProperties,
  },

  h2: {
    fontSize: pxToRem(36),
    lineHeight: 1.3,
    ...baseHeadingProperties,
  },

  h3: {
    fontSize: pxToRem(30),
    lineHeight: 1.375,
    ...baseHeadingProperties,
  },

  h4: {
    fontSize: pxToRem(24),
    lineHeight: 1.375,
    ...baseHeadingProperties,
  },

  h5: {
    fontSize: pxToRem(20),
    lineHeight: 1.375,
    ...baseHeadingProperties,
  },

  h6: {
    fontSize: pxToRem(16),
    lineHeight: 1.625,
    ...baseHeadingProperties,
  },

  subtitle1: {
    fontFamily: baseProperties.bodyFontFamily,
    fontSize: pxToRem(16),
    fontWeight: baseProperties.fontWeightMedium,
    lineHeight: 1.5,
  },

  subtitle2: {
    fontFamily: baseProperties.bodyFontFamily,
    fontSize: pxToRem(16),
    fontWeight: baseProperties.fontWeightRegular,
    lineHeight: 1.5,
  },

  body1: {
    fontFamily: baseProperties.bodyFontFamily,
    fontSize: pxToRem(16),
    fontWeight: baseProperties.fontWeightRegular,
    lineHeight: 1.5,
  },

  body2: {
    fontFamily: baseProperties.bodyFontFamily,
    fontSize: pxToRem(14),
    fontWeight: baseProperties.fontWeightRegular,
    lineHeight: 1.43,
  },

  button: {
    fontFamily: baseProperties.bodyFontFamily,
    fontSize: pxToRem(14),
    fontWeight: baseProperties.fontWeightBold,
    lineHeight: 1.43,
    textTransform: "uppercase",
  },

  caption: {
    fontFamily: baseProperties.bodyFontFamily,
    fontSize: pxToRem(12),
    fontWeight: baseProperties.fontWeightMedium,
    lineHeight: 1.33,
  },

  overline: {
    fontFamily: baseProperties.bodyFontFamily,
  },

  code: {
    fontFamily: baseProperties.codeFontFamily,
    fontSize: pxToRem(14),
    fontWeight: baseProperties.fontWeightRegular,
    lineHeight: 1.43,
    ...monoTabularProps,
  },

  mono: {
    fontFamily: baseProperties.codeFontFamily,
    fontSize: pxToRem(14),
    fontWeight: baseProperties.fontWeightRegular,
    lineHeight: 1.43,
    ...monoTabularProps,
  },

  d1: {
    fontSize: pxToRem(80),
    ...baseDisplayProperties,
  },

  d2: {
    fontSize: pxToRem(72),
    ...baseDisplayProperties,
  },

  d3: {
    fontSize: pxToRem(64),
    ...baseDisplayProperties,
  },

  d4: {
    fontSize: pxToRem(56),
    ...baseDisplayProperties,
  },

  d5: {
    fontSize: pxToRem(48),
    ...baseDisplayProperties,
  },

  d6: {
    fontSize: pxToRem(40),
    ...baseDisplayProperties,
  },

  size: {
    xxs: baseProperties.fontSizeXXS,
    xs: baseProperties.fontSizeXS,
    sm: baseProperties.fontSizeSM,
    md: baseProperties.fontSizeMD,
    lg: baseProperties.fontSizeLG,
    xl: baseProperties.fontSizeXL,
    "2xl": baseProperties.fontSize2XL,
    "3xl": baseProperties.fontSize3XL,
  },

  lineHeight: {
    sm: 1.25,
    md: 1.5,
    lg: 2,
  },

  families: {
    display: baseProperties.displayFontFamily,
    body: baseProperties.bodyFontFamily,
    code: baseProperties.codeFontFamily,
    mono: baseProperties.codeFontFamily,
  },
};

export default typography;
